import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";
import {
  CheckCircle,
  Circle,
  Funnel,
} from "@phosphor-icons/react";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "unchecked", label: "Unchecked" },
  { value: "checked", label: "Checked" },
];

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function AuditDetails({ value }) {
  const details = value || {};
  const text = JSON.stringify(details);

  return (
    <div
      className="text-xs text-zinc-600 max-w-md truncate"
      title={text}
    >
      {text}
    </div>
  );
}

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [checkingId, setCheckingId] = useState(null);

  const loadLogs = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/audit-logs", {
        params: { limit: 500 },
      });

      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const counts = useMemo(() => {
    const checked = logs.filter((log) => Boolean(log.checked_at)).length;

    return {
      all: logs.length,
      checked,
      unchecked: logs.length - checked,
    };
  }, [logs]);

  const visibleLogs = useMemo(() => {
    if (filter === "checked") {
      return logs.filter((log) => Boolean(log.checked_at));
    }

    if (filter === "unchecked") {
      return logs.filter((log) => !log.checked_at);
    }

    return logs;
  }, [logs, filter]);

  const markAsChecked = async (log) => {
    if (!log?.id || log.checked_at || checkingId) {
      return;
    }

    setCheckingId(log.id);

    try {
      const { data } = await api.patch(
        `/audit-logs/${log.id}/check`
      );

      const updatedLog = data?.audit_log;

      if (updatedLog) {
        setLogs((current) =>
          current.map((item) =>
            item.id === updatedLog.id ? updatedLog : item
          )
        );
      } else {
        await loadLogs();
      }

      toast.success(
        data?.already_checked
          ? "This audit entry was already checked."
          : "Audit entry marked as checked."
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCheckingId(null);
    }
  };

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
            Admin
          </div>

          <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">
            Audit log
          </h1>

          <p className="text-sm text-zinc-600">
            Chronological record of user actions and their review status.
          </p>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 mr-1">
              <Funnel className="w-4 h-4" />
              Filter
            </div>

            {FILTERS.map((option) => {
              const active = filter === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFilter(option.value)}
                  className={[
                    "inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-sm transition-colors",
                    active
                      ? "border-indigo-900 bg-indigo-950 text-white"
                      : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100",
                  ].join(" ")}
                >
                  {option.label}

                  <span
                    className={[
                      "rounded-full px-1.5 py-0.5 text-[10px] font-mono",
                      active
                        ? "bg-white/15 text-white"
                        : "bg-zinc-100 text-zinc-600",
                    ].join(" ")}
                  >
                    {counts[option.value]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[72vh]">
            <table className="data-table min-w-[1250px]">
              <thead className="sticky top-0 z-10 bg-white shadow-sm">
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Session</th>
                  <th>Item</th>
                  <th>Details</th>
                  <th>Checked By</th>
                </tr>
              </thead>

              <tbody>
                {visibleLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="mono text-xs whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </td>

                    <td className="mono text-xs whitespace-nowrap">
                      {log.action_type || "—"}
                    </td>

                    <td className="min-w-[180px]">
                      <div className="text-sm text-zinc-900">
                        {log.user_full_name || "—"}
                      </div>

                      {log.user_email && (
                        <div className="text-xs text-zinc-500 mono">
                          {log.user_email}
                        </div>
                      )}
                    </td>

                    <td className="text-xs uppercase tracking-wider whitespace-nowrap">
                      {log.user_role || "—"}
                    </td>

                    <td className="mono text-xs">
                      {log.session_id?.slice(0, 8) || "—"}
                    </td>

                    <td className="mono text-xs">
                      {log.item_id?.slice(0, 8) || "—"}
                    </td>

                    <td>
                      <AuditDetails value={log.new_value} />
                    </td>

                    <td className="min-w-[210px]">
                      {log.checked_at ? (
                        <div className="inline-flex items-start gap-2">
                          <CheckCircle
                            weight="fill"
                            className="w-5 h-5 mt-0.5 shrink-0 text-emerald-700"
                          />

                          <div>
                            <div className="text-sm font-semibold text-emerald-800">
                              Checked
                            </div>

                            <div className="text-xs text-zinc-700">
                              {log.checked_by_name ||
                                log.checked_by_email ||
                                "Admin"}
                            </div>

                            <div className="text-[11px] text-zinc-500 mono whitespace-nowrap">
                              {formatDateTime(log.checked_at)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => markAsChecked(log)}
                          disabled={checkingId !== null}
                          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:border-emerald-500 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Circle className="w-4 h-4" />

                          {checkingId === log.id
                            ? "Checking..."
                            : "Mark as Checked"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {!loading && visibleLogs.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-zinc-500 py-10"
                    >
                      {filter === "unchecked"
                        ? "No unchecked audit entries."
                        : filter === "checked"
                          ? "No checked audit entries."
                          : "No audit entries."}
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-zinc-500 py-10"
                    >
                      Loading audit entries…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
